import { useState, useEffect } from "react";
import { Paper } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { createTheme, ThemeProvider } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import colors from "../../theme/colors";
import { useSelector } from "react-redux";

const theme = createTheme({
  palette: {
    DataGrid: {
      headerBg: colors.grayLightest,
    },
  },
});

const EmptyTablePlaceHolder = styled.div`
  height: 100%;
  font-size: 30px;
  color: ${colors.grayLight};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ItemLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.blue};
  white-space: "nowrap";
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ItemOnClickLink = styled.div`
  color: ${colors.blue};
  white-space: "nowrap";
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const StatusTag = styled.div`
  border-radius: 15px;
  text-align: center;
  padding: 2px;
  width: 87px;
  font-weight: bold;
  background: ${colors.grayLight};
  color: ${colors.grayDark};
  border: 1px solid ${colors.gray};
  &.alarm {
    background: ${colors.redLightError};
    color: ${colors.redError};
    border: 1px solid ${colors.redError};
  }
  &.progress {
    background: ${colors.yellowLight};
    color: ${colors.yellow};
    border: 1px solid ${colors.yellow};
  }
  &.resolved {
    background: ${colors.greenLightSuccess};
    color: ${colors.greenSuccess};
    border: 1px solid ${colors.greenSuccess};
  }
`;

const TableComponent = ({
  columns,
  placeholder = "No Results",
  getRowsFunction = async () => {},
  initialState,
  label,
  isAdminView = false,
  autoResizeColumns = false,
  reloadData = false,
  rowSelectionCallback = async () => {},
  rowSelectionModelInitValues = [],
  loadingOverride = undefined,
  ...props
}) => {
  console.log("rendering datagrid");

  const { organizationId } = useParams();
  const userProfile = useSelector((state) => state.user);
  const [getData, setGetData] = useState(true);
  const [loading, setLoading] = useState(true);

  const [apiRef, setApiRef] = useState(useGridApiRef());

  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: "include",
    ids: new Set([...rowSelectionModelInitValues]),
  });

  useEffect(() => {
    console.log(rowSelectionModel);
    if (!loading) {
      rowSelectionCallback([...rowSelectionModel.ids]);
    }
  }, [rowSelectionModel]);

  useEffect(() => {
    // console.log(rowSelectionModelInitValues);
    if (rowSelectionModelInitValues.length) {
      setRowSelectionModel({
        type: "include",
        ids: new Set([...rowSelectionModelInitValues]),
      });
    }
  }, [rowSelectionModelInitValues]);

  useEffect(() => {
    if (reloadData) {
      setGetData(true);
    }
  }, [reloadData]);

  useEffect(() => {
    if (isAdminView) {
      if (userProfile.id && getData && apiRef?.current !== null) {
        async function getRows({ datagrid }) {
          const rows = await getRowsFunction({ organizationId });
          datagrid.updateRows(rows);
          if (loadingOverride !== undefined) {
            setLoading(loadingOverride);
          } else {
            setLoading(false);
          }
          setGetData(false);
          if (autoResizeColumns) {
            setTimeout(() => {
              datagrid.autosizeColumns({
                includeHeaders: true,
                includeOutliers: true,
                expand: true,
              });
            }, 0);
          }
        }
        getRows({ datagrid: apiRef.current });
      }
    } else {
      if (
        organizationId &&
        userProfile.id &&
        getData &&
        apiRef?.current !== null
      ) {
        async function getRows({ datagrid }) {
          const rows = await getRowsFunction({ organizationId });
          datagrid.updateRows(rows);
          if (loadingOverride !== undefined) {
            setLoading(loadingOverride);
          } else {
            setLoading(false);
          }

          setGetData(false);
          if (autoResizeColumns) {
            setTimeout(() => {
              datagrid.autosizeColumns({
                includeHeaders: true,
                includeOutliers: true,
                expand: true,
              });
            }, 0);
          }
        }
        getRows({ datagrid: apiRef.current });
      }
    }
  }, [userProfile, apiRef, getData]);

  const PlaceHolder = () => (
    <EmptyTablePlaceHolder>{placeholder}</EmptyTablePlaceHolder>
  );

  return (
    <Paper sx={{ padding: "20px", width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "215px",
          maxHeight: "500px",
        }}
      >
        <ThemeProvider theme={theme}>
          <DataGridPro
            {...props}
            apiRef={apiRef}
            columns={columns}
            loading={loading}
            rowHeight={35}
            columnHeaderHeight={40}
            pagination
            showToolbar
            showSettingsMenu
            label={label}
            initialState={{
              pagination: { paginationModel: { pageSize: 500 } },
              ...initialState,
            }}
            autosizeOptions={{
              includeHeaders: true,
              includeOutliers: true,
              expand: true,
            }}
            autosizeOnMount
            pageSizeOptions={[100, 250, 500, { value: -1, label: "All" }]}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            slots={{
              noRowsOverlay: PlaceHolder,
            }}
            disableRowSelectionOnClick
            disableRowSelectionExcludeModel
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
          />
        </ThemeProvider>
      </div>
    </Paper>
  );
};

export { TableComponent, ItemLink, ItemOnClickLink, StatusTag };
